import { useEffect, useRef, useState } from "react";
import { ChevronDown, SwatchBook } from "lucide-react";
import { STATUS_COLORS, CLEANING_RING } from "@/lib/constants";
import { HOUSE_COLORS, HOUSE_PATH } from "@/lib/pin";
import { useCleaningCompanies } from "@/lib/useCleaningCompanies";

function HouseGlyph({ mode }) {
  const clipId = `legend-house-${mode}`;
  if (mode === "both") {
    return (
      <svg viewBox="0 0 576 512" className="w-3.5 h-3.5 flex-shrink-0">
        <defs><clipPath id={clipId}><path d={HOUSE_PATH} /></clipPath></defs>
        <g clipPath={`url(#${clipId})`}>
          <rect width="288" height="512" fill={HOUSE_COLORS.airbnb} />
          <rect x="288" width="288" height="512" fill={HOUSE_COLORS.booking} />
        </g>
      </svg>
    );
  }
  const color = mode === "airbnb" ? HOUSE_COLORS.airbnb : mode === "booking" ? HOUSE_COLORS.booking : HOUSE_COLORS.none;
  return (
    <svg viewBox="0 0 576 512" className="w-3.5 h-3.5 flex-shrink-0">
      <path d={HOUSE_PATH} fill={color} />
    </svg>
  );
}

export default function MapLegend({ globalView = true }) {
  const [manual, setManual] = useState(null);
  const prevGlobal = useRef(globalView);
  const { names: companies } = useCleaningCompanies();

  useEffect(() => {
    if (prevGlobal.current !== globalView) {
      prevGlobal.current = globalView;
      setManual(null);
    }
  }, [globalView]);

  const open = manual !== null ? manual : globalView;

  return (
    <div data-testid="map-legend" data-open={open}
         className={`absolute bottom-6 left-3 max-md:bottom-[4.75rem] z-[1000] bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden transition-all duration-300 ease-in-out ${open ? "max-w-[348px]" : "max-w-[150px]"}`}>
      <div className="grid transition-all duration-300 ease-in-out"
           style={{ gridTemplateRows: open ? "1fr" : "0fr", opacity: open ? 1 : 0 }}>
        <div className="overflow-hidden min-h-0">
          <div className="w-[320px] px-3.5 pt-3.5 pb-1">
            <div className="text-xs font-bold text-slate-900 mb-2">Légende des marqueurs</div>
            <div className="grid grid-cols-2 gap-x-4">
              <div data-testid="map-legend-anomalie">
                <div className="text-[11px] font-bold text-slate-800 mb-1">Fond = anomalie</div>
                {Object.entries(STATUS_COLORS).map(([k, s]) => (
                  <div key={k} className="flex items-center gap-1.5 py-[3px]">
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: s.hex }} />
                    <span className="text-[11px] text-slate-600">{k === "ok" ? "Aucune" : s.label}</span>
                  </div>
                ))}
              </div>
              <div data-testid="map-legend-menage">
                <div className="text-[11px] font-bold text-slate-800 mb-1">Anneau = ménage</div>
                {companies.map((c) => (
                  <div key={c} className="flex items-center gap-1.5 py-[3px]">
                    <span className="w-3 h-3 rounded-full flex-shrink-0 bg-white"
                          style={{ border: `2.5px solid ${CLEANING_RING[c]?.hex || "#9B9B9D"}` }} />
                    <span className="text-[11px] text-slate-600 truncate">{c}</span>
                  </div>
                ))}
                <div className="flex items-center gap-1.5 py-[3px]">
                  <span className="w-3 h-3 rounded-full flex-shrink-0 bg-white" style={{ border: "2.5px solid #9B9B9D" }} />
                  <span className="text-[11px] text-slate-600">Non renseigné</span>
                </div>
              </div>
            </div>
            <div className="border-t border-slate-100 mt-2 pt-2" data-testid="map-legend-plateformes">
              <div className="text-[11px] font-bold text-slate-800 mb-1">Maison = plateformes</div>
              <div className="grid grid-cols-2 gap-x-4">
                <div className="flex items-center gap-1.5 py-[3px]"><HouseGlyph mode="airbnb" /><span className="text-[11px] text-slate-600">Airbnb</span></div>
                <div className="flex items-center gap-1.5 py-[3px]"><HouseGlyph mode="booking" /><span className="text-[11px] text-slate-600">Booking</span></div>
                <div className="flex items-center gap-1.5 py-[3px]"><HouseGlyph mode="both" /><span className="text-[11px] text-slate-600">Airbnb + Booking</span></div>
                <div className="flex items-center gap-1.5 py-[3px]"><HouseGlyph mode="none" /><span className="text-[11px] text-slate-600">Aucune des deux</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <button onClick={() => setManual(!open)} data-testid="map-legend-toggle"
              className="w-full flex items-center justify-between gap-2 px-3.5 py-2.5 hover:bg-slate-50 transition-colors">
        <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 whitespace-nowrap">
          <SwatchBook className="w-3.5 h-3.5 text-slate-500" />Légende
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform duration-300 ${open ? "" : "rotate-180"}`} />
      </button>
    </div>
  );
}
